import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldAlert, Users, Package, AlertTriangle, CheckCircle, Ban } from 'lucide-react';

const RuleItem = ({ icon: Icon, color = 'text-rm-red', children }) => (
  <li className="flex items-start gap-3 py-3 border-b border-[#1a1a1a] last:border-0">
    <Icon size={15} className={`${color} shrink-0 mt-0.5`} />
    <span className="text-sm text-zinc-400 leading-relaxed">{children}</span>
  </li>
);

const Section = ({ icon: Icon, title, accent, children }) => (
  <div className="bg-[#0d0d0d] border border-[#1e1e1e] rounded-sm overflow-hidden mb-6">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-[#1e1e1e]">
      <div className={`w-9 h-9 flex items-center justify-center rounded-sm ${accent}`}>
        <Icon size={18} className="text-white" />
      </div>
      <h2 className="font-rajdhani font-bold text-xl uppercase tracking-wide">{title}</h2>
    </div>
    <ul className="px-6 py-2">{children}</ul>
  </div>
);

export default function Rules() {
  return (
    <div className="min-h-screen bg-[#080808]">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 animate-fade-in">
        {/* Header */}
        <div className="mb-10">
          <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-2">Marketplace</p>
          <h1 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Platform <span className="text-rm-red">Rules</span>
          </h1>
          <p className="text-zinc-500 text-sm leading-relaxed">
            By using RobloxMart you agree to follow these rules. Violations can result in warnings, listing removal, or a permanent ban.
            RobloxMart is not affiliated with Roblox Corporation and does not process payments.
          </p>
        </div>

        {/* Global Rules */}
        <Section icon={Shield} title="Global Rules" accent="bg-rm-red/80">
          <RuleItem icon={AlertTriangle} color="text-yellow-500">
            <strong className="text-white">All deals must be conducted through a Middleman (MM).</strong> You must use either a Halal MM
            (a trusted community middleman) or our official RobloxMart MM service. Refusing to use an MM can result in a ban.
          </RuleItem>
          <RuleItem icon={Ban} color="text-rm-red">
            <strong className="text-white">No scamming under any circumstances.</strong> Even an attempt to scam, including bait-and-switch
            deals or cancelling after receiving payment, can lead to an immediate permanent ban.
          </RuleItem>
          <RuleItem icon={Ban} color="text-rm-red">
            <strong className="text-white">No real money trading (RMT).</strong> All transactions must involve in-game items or currencies
            only. Advertising real-money exchanges is strictly prohibited and will result in a permanent ban.
          </RuleItem>
          <RuleItem icon={ShieldAlert} color="text-zinc-400">
            Respect all users. Harassment, discrimination, hate speech, or threatening behavior will not be tolerated.
          </RuleItem>
          <RuleItem icon={ShieldAlert} color="text-zinc-400">
            Do not share personal information (real names, addresses, phone numbers) on the platform.
          </RuleItem>
        </Section>

        {/* Buyer Rules */}
        <Section icon={Users} title="Buyer Rules" accent="bg-blue-600/80">
          <RuleItem icon={CheckCircle} color="text-green-500">
            Always request an MM before initiating any trade. If a seller refuses to use an MM, report them immediately using the
            "Report" button on their listing.
          </RuleItem>
          <RuleItem icon={CheckCircle} color="text-green-500">
            When leaving a review, provide honest and accurate feedback. Reviews must include valid screenshot proof of the trade.
            False reviews are subject to removal and account suspension.
          </RuleItem>
          <RuleItem icon={AlertTriangle} color="text-yellow-500">
            Contact reveals are rate-limited to prevent abuse. Do not spam sellers or reveal contacts for listings you have no
            genuine interest in purchasing.
          </RuleItem>
          <RuleItem icon={ShieldAlert} color="text-zinc-400">
            If you suspect a listing is fraudulent or a seller has scammed, report it immediately. Do not attempt to retaliate
            or handle disputes outside the platform.
          </RuleItem>
        </Section>

        {/* Seller Rules */}
        <Section icon={Package} title="Seller Rules" accent="bg-purple-600/80">
          <RuleItem icon={CheckCircle} color="text-green-500">
            Always agree to use a Middleman (MM) when requested by the buyer. Refusing MM is a bannable offence.
          </RuleItem>
          <RuleItem icon={CheckCircle} color="text-green-500">
            Listings must be accurate, truthful, and up-to-date. The item you list must match exactly what you deliver.
            Misrepresenting items (wrong rarity, wrong type, wrong game) is prohibited.
          </RuleItem>
          <RuleItem icon={AlertTriangle} color="text-yellow-500">
            No duplicate or spam listings. Do not create multiple listings for the same item to inflate visibility. Use the
            <strong className="text-white"> Bump</strong> feature instead to re-promote existing listings.
          </RuleItem>
          <RuleItem icon={AlertTriangle} color="text-yellow-500">
            Keep your contact methods up-to-date in your dashboard. Buyers rely on accurate contact info to complete trades.
          </RuleItem>
          <RuleItem icon={Ban} color="text-rm-red">
            No advertising, sharing referral links, or promoting external platforms within listings or descriptions.
          </RuleItem>
        </Section>

        {/* Enforcement */}
        <div className="bg-rm-red/5 border border-rm-red/20 rounded-sm p-5 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-rm-red shrink-0 mt-0.5" />
            <div>
              <h3 className="font-rajdhani font-bold text-base uppercase tracking-wide mb-1.5">Enforcement</h3>
              <p className="text-sm text-zinc-500 leading-relaxed">
                Rule violations are handled by our admin team. Penalties range from warnings and listing removal to
                permanent account and IP bans. Banned users receive an email with the reason for their ban.
                If you believe your ban is unjust, contact us at{' '}
                <a href="mailto:support@robloxmart.store" className="text-rm-red hover:underline">support@robloxmart.store</a>.
              </p>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <div className="flex items-center gap-4 text-sm text-zinc-600">
          <Link to="/tos" className="hover:text-rm-red transition-colors">Terms of Service</Link>
          <span>·</span>
          <Link to="/feedback" className="hover:text-rm-red transition-colors">Report an Issue</Link>
          <span>·</span>
          <Link to="/browse" className="hover:text-rm-red transition-colors">Browse Listings</Link>
        </div>
      </div>
    </div>
  );
}
